import { useEffect, useState } from 'react';
import { Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { useUser } from '../../utils/hook/UserContext';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import moment from 'moment';
import { useAxios } from '../../utils/hook/useAxios';

// Longueurs IBAN officielles par pays (ISO 13616)
const IBAN_LENGTHS = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FK: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NI: 28,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
};

function validateIBAN(raw) {
  const iban = raw.replace(/\s/g, '').toUpperCase();
  if (iban.length < 4) return null; // pas encore assez pour juger

  const country = iban.slice(0, 2);
  const expectedLen = IBAN_LENGTHS[country];

  if (!expectedLen) return 'Pays non reconnu (2 premières lettres invalides).';
  if (iban.length < expectedLen)
    return `IBAN incomplet : ${iban.length}/${expectedLen} caractères pour ${country}.`;
  if (iban.length > expectedLen)
    return `IBAN trop long : ${iban.length}/${expectedLen} caractères pour ${country}.`;
  if (!/^[A-Z0-9]+$/.test(iban)) return "L'IBAN ne doit contenir que des lettres et des chiffres.";

  // Vérification MOD-97
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged
    .split('')
    .map((c) => (c >= 'A' ? c.charCodeAt(0) - 55 : c))
    .join('');
  let remainder = 0;
  for (const char of numeric) {
    remainder = (remainder * 10 + parseInt(char, 10)) % 97;
  }
  if (remainder !== 1) return 'IBAN invalide (clé de contrôle incorrecte).';

  return ''; // valide
}

function AjouterIBAN(props) {
  const axios = useAxios();
  const me = JSON.parse(window.localStorage.getItem('me'));

  const [iban, setIban] = useState('');
  const [ibanWarning, setIbanWarning] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTestError, setIsTestError] = useState(false);
  const [success, setSuccess] = useState(false);

  const TEST_IBAN = 'FR1420041010050500013M02606';

  const applyIban = (value) => {
    const clean = value.toUpperCase().replace(/\s/g, '');
    setIban(clean);
    setIbanWarning(validateIBAN(clean));
  };

  const handleClose = () => {
    setIban('');
    setIbanWarning(null);
    setError(null);
    setIsTestError(false);
    setSuccess(false);
    props.onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsTestError(false);
    setIsLoading(true);
    try {
      await axios.post('/stripe/add-bank-account', { iban });
      setSuccess(true);
      if (props.onSuccess) props.onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (msg.toLowerCase().includes('test bank account')) {
        setIsTestError(true);
      } else {
        setError(msg || 'Une erreur est survenue. Vérifiez votre IBAN et réessayez.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const popover = (
    <Popover>
      <Popover.Body>
        Moovicar utilise Stripe pour sécuriser les paiements et transférer automatiquement vos
        revenus vers votre compte bancaire.
      </Popover.Body>
    </Popover>
  );

  return (
    <Modal {...props} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Recevoir mes paiements</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="text-center mb-3">
          <i className="bi bi-bank" style={{ fontSize: '50px' }}></i>
        </div>

        {success ? (
          <div className="text-center">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '48px' }}></i>
            <h5 className="mt-3">IBAN enregistré avec succès</h5>
            <p className="text-muted">Vos revenus seront versés sur ce compte bancaire.</p>
            <button className="btn btn-success w-100" onClick={handleClose}>
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h5 className="text-center">Connectez votre compte bancaire</h5>
            <p className="text-muted text-center">
              Saisissez votre IBAN pour recevoir vos revenus de location.
            </p>

            <div className="mb-3">
              <label className="form-label fw-semibold">IBAN</label>
              <input
                type="text"
                className={`form-control ${
                  iban.length >= 4 ? (ibanWarning === '' ? 'is-valid' : 'is-invalid') : ''
                }`}
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                value={iban
                  .replace(/\s/g, '')
                  .replace(/(.{4})/g, '$1 ')
                  .trim()}
                onChange={(e) => applyIban(e.target.value)}
                required
                disabled={isLoading}
                maxLength={42}
              />
              {ibanWarning === '' ? (
                <div className="valid-feedback d-block">
                  <i className="bi bi-check-circle me-1"></i>
                  Format IBAN valide.
                </div>
              ) : ibanWarning ? (
                <div className="invalid-feedback d-block">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {ibanWarning}
                </div>
              ) : (
                <div className="form-text text-muted">
                  Ex test : FR14 2004 1010 0505 0001 3M02 606
                </div>
              )}
            </div>

            {isTestError && (
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Mode test Stripe :</strong> seuls des IBAN de test sont acceptés. Utilisez
                celui-ci :{' '}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning ms-1 font-monospace"
                  onClick={() => applyIban(TEST_IBAN)}
                >
                  FR1420041010050500013M02606
                </button>
              </div>
            )}

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="alert alert-info">
              Les paiements sont versés généralement sous <b>3 jours ouvrables</b> après la fin
              d'une location.
            </div>

            <p className="text-center text-muted small">
              Optimisé par Stripe{' '}
              <OverlayTrigger trigger="click" placement="top" overlay={popover}>
                <i className="bi bi-info-circle"></i>
              </OverlayTrigger>
            </p>

            <button
              type="submit"
              className="btn btn-primary btn-lg w-100"
              disabled={isLoading || ibanWarning !== ''}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer mon IBAN'
              )}
            </button>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
}

function Earnings() {
  const axios = useAxios();
  const [modalShow, setModalShow] = useState(false);
  const userData = JSON.parse(window.localStorage.getItem('me'));
  const { user, loading, isAuthenticated } = useUser();

  const [transactions, setTransactions] = useState([]);

  const fetchUser = async () => {
    try {
      const resp = await axios.get(`/users/searchbyid/${userData.id}`);
      if (resp.data) {
      }
    } catch (error) {
      console.error('Erreur chargement user :', error);
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(`/transactions/host/${userData.id}`);
        if (response.data) {
          const sorted = [...response.data].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setTransactions(sorted);
        }
      } catch (error) {
        console.error('Erreur chargement transactions :', error);
      }
    };

    fetchTransactions();
    fetchUser();
  }, []);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fmtEur = (value) => {
    const n = Number(value);
    if (n >= 1_000_000)
      return `${(n / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} M€`;
    if (n >= 1_000)
      return `${(n / 1_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} k€`;
    return `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  };

  const statusLabel = {
    pending: { label: 'En attente', cls: 'warning' },
    paid: { label: 'Versé', cls: 'success' },
    failed: { label: 'Échec', cls: 'danger' },
  };

  const availableYears = [
    ...new Set(transactions.map((t) => new Date(t.createdAt).getFullYear())),
  ].sort((a, b) => b - a);

  const filteredTransactions = transactions.filter(
    (t) => new Date(t.createdAt).getFullYear() === selectedYear
  );

  const total = filteredTransactions.reduce((sum, t) => sum + Number(t.amount), 0);

  const nextArrival = transactions
    .filter((t) => t.status === 'pending' && t.arrivalDate)
    .sort((a, b) => new Date(a.arrivalDate) - new Date(b.arrivalDate))[0]?.arrivalDate;

  const exportCSV = () => {
    const headers = [
      'ID',
      'Type',
      'Réservation',
      'Début',
      'Fin',
      'Créé le',
      'Arrivée prévue',
      'Montant',
      'Devise',
      'Statut',
    ];
    const rows = filteredTransactions.map((t) => [
      t.id,
      t.type,
      t.reservationId,
      t.Reservation ? moment(t.Reservation.startDate).format('DD/MM/YYYY') : '',
      t.Reservation ? moment(t.Reservation.endDate).format('DD/MM/YYYY') : '',
      moment(t.createdAt).format('DD/MM/YYYY HH:mm'),
      t.arrivalDate ? moment(t.arrivalDate).format('DD/MM/YYYY') : '',
      Number(t.amount).toFixed(2),
      t.currency?.toUpperCase(),
      statusLabel[t.status]?.label ?? t.status,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `releve-moovicar-${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mt-5">
      <h1 className="mb-4">Paiements</h1>

      {/* résumé revenus */}

      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Revenus totaux</small>
              <h3 className="text-success">{fmtEur(total)}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Prochain versement</small>
              <h3>{nextArrival ? moment(nextArrival).format('DD MMM YYYY') : '--'}</h3>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Compte bancaire</small>
              {user?.bankLast4 ? (
                <h5 className="mt-1">•••• {user.bankLast4}</h5>
              ) : (
                <p className="text-muted small mt-1 mb-0">Aucun IBAN enregistré</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* statut paiement */}

      {user?.stripeBankAccountId ? (
        <div className="alert alert-success d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <i className="bi bi-check-circle me-2"></i>
            <div>
              <strong>Compte bancaire enregistré</strong>
              <br />
              <span className="font-monospace">•••• •••• •••• {user.bankLast4}</span>
            </div>
          </div>
          <button
            className="btn btn-sm btn-outline-success ms-3"
            title="Modifier le compte bancaire"
            onClick={() => setModalShow(true)}
          >
            <i className="bi bi-pencil me-1"></i>
            Modifier
          </button>
        </div>
      ) : (
        <>
          <div className="alert alert-warning d-flex align-items-center mb-4">
            <i className="bi bi-exclamation-circle me-2"></i>
            <div>
              <strong>Aucun compte bancaire enregistré</strong>
              <br />
              Ajoutez votre IBAN pour recevoir vos revenus.
            </div>
          </div>
          <button className="btn btn-success text-white mb-4" onClick={() => setModalShow(true)}>
            <i className="bi bi-bank me-2"></i>
            Ajouter un compte bancaire
          </button>
        </>
      )}

      <AjouterIBAN show={modalShow} onHide={() => setModalShow(false)} onSuccess={fetchUser} />

      {/* historique */}

      <Box sx={{ p: 3 }}>
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <Typography variant="h4">
            Historique des transactions ({filteredTransactions.length})
          </Typography>

          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: '110px' }}
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.length > 0 ? (
                availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))
              ) : (
                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
              )}
            </select>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={exportCSV}
              disabled={filteredTransactions.length === 0}
              title="Télécharger le relevé annuel (CSV)"
            >
              <i className="bi bi-download me-1"></i>
              Relevé {selectedYear}
            </button>
          </div>
        </div>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Type</strong>
                </TableCell>
                <TableCell>
                  <strong>Réservation</strong>
                </TableCell>
                <TableCell>
                  <strong>Période</strong>
                </TableCell>
                <TableCell>
                  <strong>Créé le</strong>
                </TableCell>
                <TableCell>
                  <strong>Arrivée prévue</strong>
                </TableCell>
                <TableCell>
                  <strong>Montant</strong>
                </TableCell>
                <TableCell>
                  <strong>Statut</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <span className="text-muted">Aucune transaction pour {selectedYear}.</span>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((row) => {
                  const s = statusLabel[row.status] ?? {
                    label: row.status,
                    cls: 'secondary',
                  };
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <span className="text-capitalize">{row.type}</span>
                      </TableCell>

                      <TableCell>
                        <span className="font-monospace">#{row.reservationId}</span>
                      </TableCell>

                      <TableCell>
                        {row.Reservation ? (
                          <span className="text-muted small">
                            {moment(row.Reservation.startDate).format('DD/MM/YY')}
                            {' → '}
                            {moment(row.Reservation.endDate).format('DD/MM/YY')}
                          </span>
                        ) : (
                          '--'
                        )}
                      </TableCell>

                      <TableCell>{moment(row.createdAt).calendar()}</TableCell>

                      <TableCell>
                        {row.arrivalDate ? moment(row.arrivalDate).format('DD MMM YYYY') : '--'}
                      </TableCell>

                      <TableCell>
                        <span className="text-success fw-bold">+ {fmtEur(row.amount)}</span>
                      </TableCell>

                      <TableCell>
                        <span className={`badge bg-${s.cls}`}>{s.label}</span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <p className="text-muted small mt-3">
          Les revenus affichés correspondent au montant net après commission Moovicar. Pour vos
          obligations fiscales, téléchargez votre relevé annuel via le bouton{' '}
          <strong>Relevé {selectedYear}</strong> ci-dessus.
        </p>
      </Box>
    </div>
  );
}

export default Earnings;
